import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Divider, Drawer, Form as AntForm, Input, Segmented, Select, Space } from 'antd';
import { Plus } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { translationsApi } from '@api/translations';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { Translator } from '@/i18n/config';
import { recordAuditAction } from '@features/audit/recordAction';
import { useActiveLanguages, pickDefaultLanguage } from '@features/languages/useLanguages';
import type { Language } from '@features/languages/types';
import type { TranslationKey, TranslationStatus } from '../types';
import { TRANSLATION_FORMAT } from '../types';

// Accepts BOTH flat UPPER_SNAKE (EV_WORLD_TITLE) and dotted/kebab (ev-world.title):
// segments of [A-Za-z0-9]+ joined by '.', '_' or '-'.
const KEY_RULE = /^[A-Za-z0-9]+(?:[._-][A-Za-z0-9]+)*$/;

// System default language. Used as the create-form pre-selection ONLY when the
// active-language catalogue hasn't resolved yet — the picker itself always binds
// to the real active languages once loaded.
const FALLBACK_LANGUAGE = 'tr';

const FORMAT_VALUES = [TRANSLATION_FORMAT.TEXT, TRANSLATION_FORMAT.MARKDOWN] as const;
const STATUS_VALUES = ['DRAFT', 'PUBLISHED'] as const;

const DEFAULT_NAMESPACE = 'general';

/** Keep namespaces tidy so the public buckets don't fragment (ev-world ≠ EvWorld). */
function normalizeNamespace(raw: string): string {
  return raw.trim().toLowerCase();
}

function makeSchema(t: Translator) {
  return z.object({
    // Optional — left blank it falls back to the default bucket on save (a key must
    // live under *some* namespace because the public route is /public/i18n/{lng}/{ns}).
    namespace: z.string().max(80),
    key: z.string().min(1).max(160).regex(KEY_RULE, t('validation.keyFormat')),
    languageCode: z.string().min(2).max(10),
    value: z.string().min(1, t('validation.valueMin')).max(4000),
    description: z.string().max(500).optional(),
    // Stored presentation hint; the editor stays a plain textarea for now (markdown
    // rendering is a later phase), so this is not surfaced as a control yet.
    format: z.enum(FORMAT_VALUES),
    status: z.enum(STATUS_VALUES),
  });
}
type Values = z.infer<ReturnType<typeof makeSchema>>;

/** "Dil ekle" seed — pre-fills the SAME (namespace, key) for a new language row. */
export interface TranslationSeed {
  namespace: string;
  key: string;
  languageCode: string;
}

interface Props {
  open: boolean;
  translation: TranslationKey | null;
  namespaces: string[];
  /**
   * When set (and not editing) the drawer opens in "add language" mode: namespace
   * + key are locked to the seed and only a new localized value is captured for
   * the seeded language (the model is one row per (namespace, key, languageCode)).
   */
  seed?: TranslationSeed | null;
  onClose: () => void;
}

export function TranslationDrawer({ open, translation, namespaces, seed, onClose }: Props) {
  const { t } = useTranslation(['translations', 'common', 'validation', 'errors']);
  const isEdit = !!translation;
  const isAddLanguage = !isEdit && !!seed;
  const qc = useQueryClient();
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);
  const [newNamespace, setNewNamespace] = useState('');
  const { data: languages = [] } = useActiveLanguages();

  const initialValues = useMemo<Values>(() => {
    if (translation) {
      return {
        namespace: translation.namespace,
        key: translation.key,
        languageCode: translation.languageCode,
        value: translation.value,
        description: translation.description ?? '',
        format: translation.format,
        status: translation.status,
      };
    }
    return {
      namespace: seed?.namespace ?? DEFAULT_NAMESPACE,
      key: seed?.key ?? '',
      languageCode:
        seed?.languageCode ??
        pickDefaultLanguage(languages)?.code ??
        FALLBACK_LANGUAGE,
      value: '',
      description: '',
      format: TRANSLATION_FORMAT.TEXT,
      status: 'DRAFT',
    };
    // languages intentionally omitted: it only seeds the create default and RHF
    // reads defaultValues once at mount (drawer uses destroyOnClose → fresh mount
    // per open); 'tr' is a safe fallback until the catalogue resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translation, seed]);

  const createMutation = useMutation({
    mutationFn: (v: Values) =>
      translationsApi.create({
        namespace: normalizeNamespace(v.namespace) || DEFAULT_NAMESPACE,
        key: v.key,
        languageCode: v.languageCode,
        value: v.value,
        description: v.description || undefined,
        format: v.format,
        status: v.status,
      }),
    onSuccess: (created) => {
      toast.success(t('toast.created', { key: created.key }));
      void recordAuditAction({
        action: 'translation.create',
        resource: 'translation',
        resourceId: created.id,
        summary: t('audit.create', { key: created.key }),
      });
      void qc.invalidateQueries({ queryKey: ['translations'] });
      onClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (v: Values) => {
      if (!translation) throw new Error('no translation');
      return translationsApi.update(translation.id, {
        namespace: normalizeNamespace(v.namespace) || DEFAULT_NAMESPACE,
        key: v.key,
        languageCode: v.languageCode,
        value: v.value,
        description: v.description || undefined,
        format: v.format,
        status: v.status,
      });
    },
    onSuccess: (updated) => {
      toast.success(t('toast.updated', { key: updated.key }));
      void recordAuditAction({
        action: 'translation.update',
        resource: 'translation',
        resourceId: updated.id,
        summary: t('audit.update', { key: updated.key }),
      });
      void qc.invalidateQueries({ queryKey: ['translations'] });
      onClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  useEffect(() => {
    if (!open) {
      createMutation.reset();
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const drawerTitle = isEdit
    ? t('drawer.editTitle', { key: translation?.key })
    : isAddLanguage
      ? t('drawer.addLanguageTitle', { key: seed?.key })
      : t('drawer.createTitle');

  return (
    <Drawer
      title={drawerTitle}
      open={open}
      onClose={onClose}
      afterOpenChange={(opened) => {
        if (!opened) setNewNamespace('');
      }}
      width={Math.min(520, typeof window !== 'undefined' ? window.innerWidth : 520)}
      destroyOnClose
    >
      <Form
        schema={schema}
        defaultValues={initialValues}
        onSubmit={(v) => (isEdit ? updateMutation.mutate(v) : createMutation.mutate(v))}
      >
        {({ watch, setValue }) => {
          const namespace = watch('namespace');
          const languageCode = watch('languageCode');
          const status = watch('status');

          // Plain computation (cheap) — not a hook: this runs inside Form's render prop,
          // which is a callback, not a component, so hooks must not be called here.
          const namespaceOptions = Array.from(
            new Set<string>([DEFAULT_NAMESPACE, ...namespaces, namespace].filter(Boolean)),
          )
            .sort((a, b) => a.localeCompare(b))
            .map((ns) => ({ value: ns, label: ns }));

          // Bind the language picker to the real active-language catalogue. Always
          // include the current value so a legacy row on a now-inactive language
          // still shows a matching option instead of a blank select.
          const languageOptions = buildLanguageOptions(languages, languageCode);

          const addNamespace = () => {
            const norm = normalizeNamespace(newNamespace);
            if (!norm) return;
            setValue('namespace', norm, { shouldDirty: true, shouldValidate: true });
            setNewNamespace('');
          };

          return (
            <>
              {isEdit ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={t('drawer.editKeyWarning')}
                />
              ) : null}
              {isAddLanguage ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={t('drawer.addLanguageHint', {
                    namespace: seed?.namespace,
                    key: seed?.key,
                  })}
                />
              ) : null}
              <AntForm layout="vertical" component="div">
                <AntForm.Item label={t('form.labels.namespace')} help={t('form.namespaceHelp')}>
                  <Select
                    size="large"
                    showSearch
                    allowClear
                    disabled={isAddLanguage}
                    value={namespace || undefined}
                    options={namespaceOptions}
                    onChange={(v) =>
                      setValue('namespace', v ?? '', { shouldDirty: true, shouldValidate: true })
                    }
                    placeholder={t('form.placeholders.namespace')}
                    style={{ width: '100%' }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space.Compact style={{ padding: '0 8px 4px', width: '100%' }}>
                          <Input
                            size="small"
                            placeholder={t('form.namespaceNewPlaceholder')}
                            value={newNamespace}
                            onChange={(e) => setNewNamespace(e.target.value)}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addNamespace();
                              }
                            }}
                          />
                          <Button
                            size="small"
                            type="text"
                            icon={<Plus size={14} />}
                            onClick={addNamespace}
                          >
                            {t('form.namespaceAddConfirm')}
                          </Button>
                        </Space.Compact>
                      </>
                    )}
                  />
                </AntForm.Item>

                <FormField name="key" label={t('form.labels.key')} required>
                  <Input size="large" disabled={isAddLanguage} placeholder={t('form.placeholders.key')} />
                </FormField>

                <AntForm.Item label={t('form.labels.languageCode')} help={t('form.languageHelp')}>
                  <Select
                    size="large"
                    showSearch
                    optionFilterProp="label"
                    value={languageCode}
                    options={languageOptions}
                    onChange={(v) =>
                      setValue('languageCode', v, { shouldDirty: true, shouldValidate: true })
                    }
                    style={{ width: '100%' }}
                  />
                </AntForm.Item>

                <FormField name="value" label={t('form.labels.value')} required>
                  <Input.TextArea
                    rows={4}
                    maxLength={4000}
                    showCount
                    placeholder={t('form.placeholders.value')}
                  />
                </FormField>

                <AntForm.Item label={t('form.labels.status')}>
                  <Segmented<TranslationStatus>
                    value={status}
                    onChange={(v) =>
                      setValue('status', v, { shouldDirty: true, shouldValidate: true })
                    }
                    options={[
                      { value: 'DRAFT', label: t('status.DRAFT') },
                      { value: 'PUBLISHED', label: t('status.PUBLISHED') },
                    ]}
                  />
                </AntForm.Item>

                <FormField name="description" label={t('form.labels.description')}>
                  <Input.TextArea
                    rows={2}
                    maxLength={500}
                    showCount
                    placeholder={t('form.placeholders.description')}
                  />
                </FormField>
              </AntForm>
              <Space style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {isEdit ? t('common:actions.save') : t('common:actions.create')}
                </Button>
                <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
              </Space>
            </>
          );
        }}
      </Form>
    </Drawer>
  );
}

/** Active languages as `{name} (code)` options, guaranteeing `current` is present. */
function buildLanguageOptions(
  languages: readonly Language[],
  current: string,
): { value: string; label: string }[] {
  const options = languages.map((l) => ({ value: l.code, label: `${l.name} (${l.code})` }));
  if (current && !options.some((o) => o.value === current)) {
    options.push({ value: current, label: current });
  }
  return options;
}
